import React, { useMemo, useRef, useCallback, useState, useEffect, useLayoutEffect, useContext } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import parse from "html-react-parser";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogProps,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import SmartphoneRoundedIcon from "@mui/icons-material/SmartphoneRounded";
import SignalCellularAltOutlinedIcon from "@mui/icons-material/SignalCellularAltOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { printHtmlContent } from "../../../shared/iosPwaPrint";
import html2pdf from "html2pdf.js";
import { PreferencesContext } from "../../../PreferenceContext";
import { OPD } from "../../../services/models/OPD/OPD";
import { GetOPDDetail, GetOPDList } from "../../../services/OPDService";
import { AlertType } from "../../../services/models/AlertType";
import { getLabel, lastOpdVisitTime, dataURLtoFile } from "../../../shared/CommonFunction";
import { temperatureOptions } from "../../../shared/Data/FrontDesk/Appointment";
import { useLocation, useNavigate } from "react-router-dom";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import GrowthChart from "../../../components/Chart/GrowthChart";
import { diagnosisOptions } from "../../../shared/Model/OPD/Diagnosis";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetOPDCheifComplaintsDetail } from "../../../services/OPDChiefComplaint";
import { GetPatientHistoryOfSummaryDetail } from "../../../services/PatientHistoryOfSummary";
import { GetOPDExaminationSummaryDetail } from "../../../services/OPDExaminationSummaryService";
import { GetOPDInvestigationDetailById } from "../../../services/OPDInvestigationService";
import { GetOPDDiagnosisDetailById } from "../../../services/OPDDiagnosisService";
import { GetOpdDetailsForReport } from "../../../services/ReportService";
import { GetPatientReportCertificatePreview } from "../../../services/CertificateService";
import {
  UpdateConfiguration,
  CreateConfiguration,
} from "../../../services/SystemConfigurationService";
import { UpdateDefaultPrescription } from "../../../services/UserService";
import { zoomOptions } from "./OPDClinical.constants";
import { prescriptionTemplates } from "../../../shared/Data/ReportTemplates";
import OPDClinicalDialogs from "./Components/OPDClinicalDialogs";
import ReportCertificateHeader from "../Certificates/ReportCertificateHeader";
import ReportCertificateFooter from "../Certificates/ReportCertificateFooter";
import ProgressBar from "../../../components/Common/ProgressBar";

// SCHEMA FOR PARAMETER
interface Parameter {
  departmentId: string;
  branchId: string;
  orgId: string;
  consumerId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

const parameters: Parameter = {
  departmentId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  orgId: "",
  consumerId: "",
};

const PREVIEW_TABS = [
  { key: "CHIEF_COMPLAINT", label: "Chief Complaints" },
  { key: "HISTORY", label: "History" },
  { key: "EXAMINATION", label: "Examination" },
  { key: "VACCINATION", label: "Vaccination" },
  { key: "INVESTIGATION", label: "Investigation" },
  { key: "DIAGNOSIS", label: "Diagnosis" },
  { key: "PRESCRIPTION", label: "Prescription" },
  { key: "FOLLOW_UP", label: "Follow-Up" },
  { key: "DOCTOR_REFERRED", label: "Doctors To Be Referred" },
  { key: "NOTES", label: "Notes" },
] as const;

type PreviewTabKey = (typeof PREVIEW_TABS)[number]["key"];

interface OpdBundle {
  opd: any;
  chiefComplaintColl: any[];
  historySummaryColl: any[];
  examinationSummaryColl: any[];
  investigationSummaryColl: any[];
  investigationCommonDocuments: any[];
  investigationReportCertificateColl: any[];
  diagnosisSummaryColl: any[];
  prescriptionSummaryColl: any[];
  generalInstructionColl: string[];
  generalInstructionNotes: string[];
  followUpColl: any[];
  doctorToBeReferredColl: any[];
  notesColl: any[];
  vaccinationColl: any[];
  reportData: any;
}

export type Props = {
  opd: OPD;
};

const DEFAULT_HEADER_HEIGHT = 220;
const STICKY_TOP_OFFSET = 20;
const CLOSE_BAR_HEIGHT = 56;
const OPD_HISTORY_PAGE_SIZE = 200;

const stripHtmlTags = (value?: string) =>
  (value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatTimeStringTo12Hour = (timeStr?: string): string => {
  if (!timeStr) return "";
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return timeStr;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

const formatTime12Hour = (dt: Date): string => {
  let hours = dt.getHours();
  const minutes = String(dt.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours === 0 ? 12 : hours;
  return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

const parseCustomDateTime = (raw: string): Date | null => {
  const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  const [, day, month, year, hh, mm, ss] = match;
  const dt = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hh),
    Number(mm),
    ss ? Number(ss) : 0
  );
  return isNaN(dt.getTime()) ? null : dt;
};

const formatOpdOwnDateTime = (opdItem: any): string => {
  const raw =
    opdItem?.caseDateTime || opdItem?.opdDateTime || opdItem?.visitDateTime || opdItem?.caseDate || opdItem?.opdDate;
  if (!raw) return "";

  let dt = new Date(raw);

  if (isNaN(dt.getTime()) && typeof raw === "string") {
    const parsed = parseCustomDateTime(raw);
    if (parsed) dt = parsed;
  }

  if (isNaN(dt.getTime())) {
    const separateTime = formatTimeStringTo12Hour(opdItem?.caseTime || opdItem?.opdTime || "");
    return separateTime ? `${raw} ${separateTime}` : `${raw}`;
  }

  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = dt.getFullYear();
  const hasTimeComponent = dt.getHours() !== 0 || dt.getMinutes() !== 0 || dt.getSeconds() !== 0;
  const separateTime = formatTimeStringTo12Hour(opdItem?.caseTime || opdItem?.opdTime || "");

  if (hasTimeComponent) {
    return `${day}-${month}-${year} ${formatTime12Hour(dt)}`;
  }

  return separateTime ? `${day}-${month}-${year} ${separateTime}` : `${day}-${month}-${year}`;
};


const stripAndTrim = (v?: string) => stripHtmlTags(v || "");
const buildDocumentFiles = (docColl?: any[]) => {
  if (!Array.isArray(docColl)) return [];
  return docColl
    .filter(
      (doc: any) =>
        doc?.base64Document?.startsWith("data:") &&
        doc?.serverName &&
        doc?.isDeleted !== true,
    )
    .map((doc: any) => {
      const file = dataURLtoFile(doc.base64Document, doc.originalName || doc.serverName);
      (file as any).preview = URL.createObjectURL(file);
      (file as any).documentId = doc.documentId || 0;
      (file as any).originalName = doc.originalName || doc.serverName;
      return file;
    });
};

const buildPrescriptionFromReport = (report: any) => {
  const detailsMap = new Map<number, any>();
  (report?.prescriptionDetailsHtml || []).forEach((d: any) => detailsMap.set(d.opdPrescriptionDetailId, d));

  const stepsByDetailId = new Map<number, any[]>();
  (report?.prescriptionDetailStepsHtml || []).forEach((s: any) => {
    if (s?.isDeleted) return;
    const arr = stepsByDetailId.get(s.opdPrescriptionDetailId) || [];
    arr.push(s);
    stepsByDetailId.set(s.opdPrescriptionDetailId, arr);
  });

  const instructionsByDetailId = new Map<number, any[]>();
  (report?.prescriptionDetailMedicineInstructionsHtml || []).forEach((ins: any) => {
    if (ins?.isDeleted) return;
    const arr = instructionsByDetailId.get(ins.opdPrescriptionDetailId) || [];
    arr.push(ins);
    instructionsByDetailId.set(ins.opdPrescriptionDetailId, arr);
  });

  const orderedIds: number[] = [];
  (report?.prescriptionTable || []).forEach((row: any) => {
    if (row?.opdPrescriptionDetailId && !orderedIds.includes(row.opdPrescriptionDetailId)) {
      orderedIds.push(row.opdPrescriptionDetailId);
    }
  });
  const ids = orderedIds.length > 0 ? orderedIds : Array.from(detailsMap.keys());

  return ids.map((id) => {
    const detail = detailsMap.get(id) || {};
    const steps = stepsByDetailId.get(id) || [];
    const instructions = instructionsByDetailId.get(id) || [];

    const doseLines = steps
      .map((step: any) => {
        const doseParts = [
          step?.morningDoseDisplayValue,
          step?.afternoonDoseDisplayValue,
          step?.eveningDoseDisplayValue,
          step?.nightDoseDisplayValue,
        ].filter((v: any) => v !== null && v !== undefined && `${v}`.trim() !== "");
        const doseValueText =
          doseParts.length > 0 ? doseParts.join(" – ") : `${step?.sosDoseDisplayValue || ""}`.trim();
        const unit = detail?.printUnitLabel || "";
        const durationValue = step?.durationValue;
        const durationUnit = step?.durationUnitLabel || "";
        const hasDuration = durationValue !== null && durationValue !== undefined && Number(durationValue) > 0;
        const durationText = hasDuration ? `${durationValue} ${durationUnit}`.trim() : "";
        return [[doseValueText, unit].filter(Boolean).join(" "), durationText].filter(Boolean).join(" — ");
      })
      .filter(Boolean);

    return {
      opdPrescriptionDetailId: id,
      medicineTitle: detail?.medicineIdLabel || "",
      quantity: detail?.quantity || "",
      quantityUnit: detail?.quantityUnitLabel || "",
      doseLines,
      route: [
        detail?.routeOfAdministrationName,
        detail?.relationToFoodTimeGapName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim(),
      instructions: instructions
        .map((i: any) => stripAndTrim(i?.opdPrescriptionDetailMedicineInstructionData))
        .filter(Boolean),
    };
  });
};

const buildGeneralInstructionFromReport = (report: any) => {
  const bulletLines = (report?.prescriptionInstructionsHtml || [])
    .map((i: any) => stripAndTrim(i?.opdPrescriptionInstructionData))
    .filter(Boolean);
  const noteLines = (report?.prescriptionNotes || [])
    .filter((n: any) => n?.isInstructionNoteEnabled)
    .map((n: any) => stripAndTrim(n?.instructionNote))
    .filter(Boolean);
  return { bulletLines, noteLines };
};

const buildFollowUpFromReport = (report: any) => {
  const list = report?.followUps || [];
  const groups = new Map<
    string,
    {
      opdFollowUpId: number;
      summaryLine: string;
      instructionTexts: string[];
      notes: string[];
    }
  >();

  list.forEach((f: any) => {
    const summaryLine = [
      [f?.followUpDuration, f?.followUpDurationUnit, f?.followUpAfterText].filter(Boolean).join(" "),
      f?.followUpDay && f?.formatedFollowUpDate ? `(${f.followUpDay} ${f.formatedFollowUpDate})` : "",
      f?.followUpReason ? f.followUpReason.trim() : "",
      [f?.locationName, f?.branchName ? `(${f.branchName})` : ""].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(" - ");

    const instructionText = stripAndTrim(f?.followUpInstructionData);
    const note = stripAndTrim(f?.followUpNote);

    const groupKey = summaryLine || `__no-summary-${f?.opdid ?? Math.random()}`;
    let group = groups.get(groupKey);
    if (!group) {
      group = { opdFollowUpId: f?.opdid || 0, summaryLine, instructionTexts: [], notes: [] };
      groups.set(groupKey, group);
    }
    if (instructionText && !group.instructionTexts.includes(instructionText)) {
      group.instructionTexts.push(instructionText);
    }
    if (note && !group.notes.includes(note)) {
      group.notes.push(note);
    }
  });

  return Array.from(groups.values());
};

const buildDoctorReferredFromReport = (report: any) =>
  (report?.doctorToBeReferred || []).map((d: any) => ({
    opdDoctorToBeReferredId: d?.opdDoctorToBeReferredId,
    name: (d?.doctorToBeReferredName || "-").trim(),
    qualification: d?.qualification || "",
    hospitalName: d?.hospitalName || "",
    addressLine: [d?.address1, d?.address2, d?.city].filter(Boolean).join(", "),
    contactLine: [
      d?.mobileNumber ? `M: ${d.mobileNumber}` : "",
      d?.alternativeAppointmentNumber ? `Alt: ${d.alternativeAppointmentNumber}` : "",
      d?.email ? `E: ${d.email}` : "",
    ]
      .filter(Boolean)
      .join("   "),
    clinicalNote: stripAndTrim(d?.clinicalNote),
  }));

const SectionRow = React.memo(
  ({ title, children }: { title: React.ReactNode; children: React.ReactNode }) => (
    <Box sx={{ borderTop: "1px solid #0000001f" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: "var(--opd-panel-bg)",
          height: "40px",
          padding: "0px 20px",
          position: "static",
          visibility: "visible",
          opacity: 1,
          transform: "none",
        }}
      >
        {typeof title === "string" ? <Typography style={{ fontWeight: 600 }}>{title}</Typography> : title}
      </Box>
      <Box sx={{ padding: "5px 20px", fontSize: "13px", fontWeight: 500, backgroundColor: "var(--opd-panel-bg)" }}>
        {children}
      </Box>
    </Box>
  ),
);

const isDocumentPreviewable = (doc: any) => {
  const mime = (doc?.type || doc?.fileType || "").toLowerCase();
  return mime.startsWith("image/") || mime === "application/pdf";
};

const formatFileSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(2)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

const handleDownloadDocument = (doc: any) => {
  if (!doc?.preview) return;
  const link = document.createElement("a");
  link.href = doc.preview;
  link.download = doc?.originalName || doc?.name || "document";
  link.click();
};

const DocumentList = React.memo(({ documents }: { documents: any[] }) => {
  if (!documents || documents.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "6px" }}>
      {documents.map((doc: any, dIdx: number) => {
        const previewable = isDocumentPreviewable(doc);
        const sizeText = formatFileSize(doc?.size || doc?.fileSize);
        return (
          <Box
            key={dIdx}
            sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}
          >
            {previewable ? (
              <VisibilityIcon
                onClick={() => window.open(doc.preview, "_blank")}
                sx={{ fontSize: "18px", color: "#1976d2", cursor: "pointer", flexShrink: 0 }}
              />
            ) : (
              <VisibilityOffIcon
                sx={{ fontSize: "18px", color: "#bdbdbd", flexShrink: 0 }}
              />
            )}
            <Typography
              component="span"
              onClick={() => handleDownloadDocument(doc)}
              sx={{
                fontSize: "13px",
                color: "#1976d2",
                cursor: "pointer",
                wordBreak: "break-word",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {doc?.originalName || doc?.name}
            </Typography>
            {sizeText && (
              <Typography component="span" sx={{ fontSize: "12px", color: "#777", flexShrink: 0 }}>
                ({sizeText})
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
});

const ReportCertificateList = React.memo(
  ({
    certificates,
    onFetchData,
    headerSections,
  }: {
    certificates: any[];
    onFetchData: (
      description: string,
    ) => Promise<{ previewDescription: any; medicalReportCertificateHeader: any } | null>;
    headerSections: any;
  }) => {
    const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(new Set());
    const [loadingKey, setLoadingKey] = useState<string | number | null>(null);
    const [dataByKey, setDataByKey] = useState<Record<string | number, any>>({});
    if (!certificates || certificates.length === 0) return null;
    const handleToggle = async (cert: any, key: string | number) => {
      const isCurrentlyExpanded = expandedKeys.has(key);

      if (isCurrentlyExpanded) {
        setExpandedKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        return;
      }

      if (!dataByKey[key]) {
        setLoadingKey(key);
        const data = await onFetchData(cert?.description);
        setLoadingKey((prev) => (prev === key ? null : prev));
        if (!data) return;
        setDataByKey((prev) => ({ ...prev, [key]: data }));
      }

      setExpandedKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    };

    return (
      <Box sx={{ marginTop: "10px" }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#555", marginBottom: "6px" }}>
          Reports
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {certificates.map((cert: any, cIdx: number) => {
            const key = cert?.reportCertificateId || cIdx;
            const isExpanded = expandedKeys.has(key);
            const isLoading = loadingKey === key;
            const data = dataByKey[key];
            return (
              <Box key={key}>
                <Box
                  onClick={() => handleToggle(cert, key)}
                  sx={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
                >
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: "18px",
                      color: "#1976d2",
                      flexShrink: 0,
                      transition: "transform 0.15s ease-in-out",
                      transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#1976d2",
                      fontWeight: 600,
                      wordBreak: "break-word",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {cert?.reportCertificateName}
                  </Typography>
                  {isLoading && (
                    <Typography sx={{ fontSize: "12px", color: "#777" }}>Loading...</Typography>
                  )}
                </Box>

                {/* Inline expanded report — no footer, no print/download/zoom icons. */}
                {isExpanded && data && (
                  <Box
                    sx={{
                      marginTop: "8px",
                      marginBottom: "4px",
                      marginLeft: "24px",
                      border: "1px solid #0000001f",
                      borderRadius: "4px",
                      backgroundColor: "var(--opd-panel-bg)",
                      padding: "16px",
                      fontFamily: "Arial, sans-serif",
                      overflowX: "auto",
                    }}
                  >
                    <ReportCertificateHeader reportData={data} selectedSections={headerSections} />
                    {data?.previewDescription && (
                      <div
                        style={{ margin: "10px 0" }}
                        dangerouslySetInnerHTML={{ __html: data.previewDescription }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  },
);

const getDiagnosisSeverityStyle = (severityLevel?: any) => {
  const severityValue = typeof severityLevel === "object" ? severityLevel?.value : severityLevel;
  const severity = (diagnosisOptions as any[])?.find((x: any) => x.value === severityValue);
  return {
    bg: severity?.bg || "#ffffff",
    border: severity?.bordercolor || "#333333",
    color: severity?.color || "#222222",
  };
};

const OPDClinicalPreview: React.FC<Props> = React.memo((props) => {
  const location = useLocation();

  //HOOKS AND CONTEXT
  const theme = useTheme();
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }
  const [opdBundles, setOpdBundles] = useState<OpdBundle[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [activeOpdId, setActiveOpdId] = useState<number>(0);
  const [isLoadingNextOpd, setIsLoadingNextOpd] = useState(false);
  const [noMoreOpds, setNoMoreOpds] = useState(false);
  const [sequenceStartIndex, setSequenceStartIndex] = useState<number>(-1);
  const loadingOpdIdsRef = useRef<Set<number>>(new Set());
  const loadNextGuardRef = useRef(false);
  const failedOpdIdsRef = useRef<Set<number>>(new Set());
  const pendingScrollOpdIdRef = useRef<number | null>(null);
  const [OpdHistoryColl, setOpdHistoryColl] = useState<any[]>([]);
  const [isOpdHistoryLoading, setIsOpdHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<PreviewTabKey>("CHIEF_COMPLAINT");
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const sectionRefsByOpd = useRef<Record<number, Partial<Record<PreviewTabKey, HTMLDivElement | null>>>>({});
  const opdBlockRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const bottomSentinelRef = useRef<HTMLDivElement | null>(null);
  const opdHeaderRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [opdHeaderHeights, setOpdHeaderHeights] = useState<Record<number, number>>({});
  const historyListRef = useRef<HTMLDivElement | null>(null);
  const historyItemRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollTickingRef = useRef(false);
  const opdBundlesRef = useRef<OpdBundle[]>([]);
  useEffect(() => {
    opdBundlesRef.current = opdBundles;
  }, [opdBundles]);

  const remeasureOpdHeaders = useCallback(() => {
    setOpdHeaderHeights((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(opdHeaderRefs.current).forEach(([idStr, el]) => {
        if (!el) return;
        const id = Number(idStr);
        const h = el.offsetHeight;
        if (next[id] !== h) {
          next[id] = h;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    remeasureOpdHeaders();
    window.addEventListener("resize", remeasureOpdHeaders);
    return () => window.removeEventListener("resize", remeasureOpdHeaders);
  }, [opdBundles, remeasureOpdHeaders]);

  const [openGrowthChartDialog, setOpenGrowthChartDialog] = useState(false);
  const [growthChartBundle, setGrowthChartBundle] = useState<OpdBundle | null>(null);
  const [memberPhysicalGrowthId] = useState(0);

  const vaccinationEnableMode = useMemo(() => {
    return userContext?.systemConfigurationColl?.find(
      (item: any) =>
        item?.configModule === "VACCINATION" &&
        item?.configKey === "ENABLE_VACCINATION",
    )?.configValue;
  }, [userContext?.systemConfigurationColl]);

  const smd = useMediaQuery(theme.breakpoints.down("sm"));
  const mdd = useMediaQuery(theme.breakpoints.down("md"));
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [isLoading, setIsLoading] = useState(false);
  const [selectedOPD, setSelectedOPD] = useState<any>(null);
  const [detailedOpd, setDetailedOpd] = useState<any>(null);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [openPrescriptionReport, setOpenPrescriptionReport] = useState(false);
  const [selectedPrescriptionTemplate, setSelectedPrescriptionTemplate] =
    useState("Prescription");
  const [selectedReportTemplate, setSelectedReportTemplate] =
    useState("Prescription");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [menuAnchorEl, setMenuAnchorEl] = useState<any>(null);
  const htmlprintRef = useRef<any>();
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  const [reportPreviewData, setReportPreviewData] = useState<any>(null);
  const [reportZoomLevel, setReportZoomLevel] = useState(1);
  const reportPrintRef = useRef<any>();

  const reportConfigList = useMemo(
    () =>
      userContext?.systemConfigurationDetailColl?.filter(
        (i: any) => i?.configModule === "REPORT_CONFIG",
      ) || [],
    [userContext?.systemConfigurationDetailColl],
  );
  const [reportConfigDetail, setReportConfigDetail] = useState<any>(
    reportConfigList?.map((i: any) => ({ ...i, isChecked: i?.configValue === "1" })),
  );
  const [reportConfigData, setReportConfigData] = useState(reportConfigDetail);
  const configReport = useMemo(
    () =>
      userContext?.systemConfigurationColl?.find(
        (i: any) =>
          i?.configModule === "OPD_CLINIC" && i?.configKey === "REPORT_CONFIG",
      ),
    [userContext?.systemConfigurationColl],
  );
  const isVertical21cm = selectedReportTemplate === "Vertical21cm";
  const showFullPageLoader = isInitialLoading && opdBundles.length === 0;
  const certificateReportConfigDetail = useMemo(() => {
    return (
      userContext?.systemConfigurationDetailColl?.filter(
        (item: any) => item?.configModule === "CERTIFICATE_REPORT_CONFIG",
      ) || []
    ).map((item: any) => ({ ...item, isChecked: item?.configValue === "1" }));
  }, [userContext?.systemConfigurationDetailColl]);
  const certificateHeaderSelectedSections = useMemo(() => {
    return certificateReportConfigDetail.reduce((acc: any, item: any) => {
      acc[item.configKey] = item.isChecked;
      return acc;
    }, {});
  }, [certificateReportConfigDetail]);

  const fetchDetailedOpdforReport = async (item: any, reportName: string) => {
    try {
      const params = new URLSearchParams({
        reportName,
        reportType: "pdf",
        opdId: item?.opdId?.toString() || "",
        consumerId: userContext.consumerId.toString(),
        branchId: userContext.branchId.toString(),
        organizationId: userContext.organizationId.toString(),
        departmentId: item?.departmentId?.toString() || "",
        specialityId: item?.specialityId?.toString() || "",
        patientId: item?.patientId?.toString() || "",
      });
      const result: any = await GetOpdDetailsForReport(params);
      const reportData = result?.data?.data || null;
      setDetailedOpd(reportData);
      return reportData;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message || "Unable to load report",
        alertType: AlertType.Error,
      });
      return null;
    }
  };

  const fetchReportPayloadForPreview = async (opdItem: any) => {
    try {
      const params = new URLSearchParams({
        reportName: userContext?.defaultPrescriptionTemplate || "Prescription",
        reportType: "pdf",
        opdId: opdItem?.opdId?.toString() || "",
        consumerId: userContext.consumerId.toString(),
        branchId: userContext.branchId.toString(),
        organizationId: userContext.organizationId.toString(),
        departmentId: opdItem?.departmentId?.toString() || "",
        specialityId: opdItem?.specialityId?.toString() || "",
        patientId: opdItem?.patientId?.toString() || "",
      });
      const result: any = await GetOpdDetailsForReport(params);
      return result?.data?.data || null;
    } catch {
      return null;
    }
  };

  const handleOpenDialog = () => setOpenPrescriptionReport(true);
  const handleCloseHTMLPopup = () => setOpenPrescriptionReport(false);
  const handleClickOpenPrescriptionReports = async (
    scrollType: DialogProps["scroll"],
    item: any,
    reportName: string,
    closeDialog: boolean = true,
  ) => {
    setIsLoading(true);
    setSelectedReportTemplate(reportName);
    if (closeDialog) setOpenTemplateDialog(false);
    try {
      const data = await fetchDetailedOpdforReport(item, reportName);
      if (data) {
        setSelectedPrescriptionTemplate(reportName || "Prescription");
        handleOpenDialog();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintOpd = async (bundle: OpdBundle) => {
    const opdItem = bundle.opd;
    if (!opdItem?.opdId) return;
    setSelectedOPD(opdItem);
    const defaultTemplate = userContext?.defaultPrescriptionTemplate || "Prescription";
    const data = await fetchDetailedOpdforReport(opdItem, defaultTemplate);
    if (data) {
      setSelectedReportTemplate(defaultTemplate);
      setSelectedPrescriptionTemplate(defaultTemplate);
      handleOpenDialog();
    }
  };

  const handleOpenTemplateSelector = (bundle: OpdBundle) => {
    if (!bundle?.opd?.opdId) return;
    setSelectedOPD(bundle.opd);
    setOpenTemplateDialog(true);
  };

  const saveDefaultPrescription = async (name: any) => {
    try {
      await UpdateDefaultPrescription({
        defaultPrescription: name,
        consumerId: userContext.consumerId || 0,
        userId: userContext.loginDetail.userId || 0,
      });
      updateUserContext({ ...userContext, defaultPrescriptionTemplate: name });
    } catch (e) { }
  };

  const handleZoomChange = (e: any) => setZoomLevel(e.target.value);
  const handleZoomOut = () => setZoomLevel((p) => p - 0.25);
  const handleZoomIn = () => setZoomLevel((p) => p + 0.25);
  const handleSettingsOpen = (e: any) => setMenuAnchorEl(e.currentTarget);
  const handleSettingsClose = () => setMenuAnchorEl(null);
  const handleSettingsSave = async () => {
    try {
      const detail = reportConfigData.map((c: any) => ({
        ...c,
        mode: c.systemConfigurationDetailId > 0 ? "U" : "I",
        configValue: c.isChecked ? "1" : "0",
      }));
      const payload = {
        systemConfigurationId: configReport?.systemConfigurationId || 0,
        systemConfigurationDefaultId: configReport?.systemConfigurationDefaultId || 0,
        consumerId: userContext.consumerId,
        organizationId: userContext.organizationId,
        branchId: userContext.branchId,
        userId: userContext.loginDetail.userId,
        dataManagedBy: userContext.loginDetail.userId,
        configModule: "OPD_CLINIC",
        configKey: "REPORT_CONFIG",
        systemConfigurationDetailJson: JSON.stringify(detail),
      };
      const res: any =
        (configReport?.systemConfigurationId ?? 0) > 0
          ? await UpdateConfiguration(payload)
          : await CreateConfiguration(payload);
      if (res) {
        setReportConfigDetail(reportConfigData);
        setMenuAnchorEl(null);
      }
    } catch (e) { }
  };

  const handlePdfPrint = () => {
    if (!htmlprintRef.current) return;
    const pageMargin = isVertical21cm ? "7.5cm 1.2cm 2cm 2cm" : "0.80in";
    printHtmlContent(htmlprintRef.current, {
      printable: htmlprintRef.current.innerHTML,
      type: "raw-html",
      maxWidth: 1000,
      style: `
        @media print {
          @page { size: auto; margin: ${pageMargin}; }
          body {
            padding: 0;
            background-color: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            font-family: Arial, sans-serif !important;
          }
          .print-special-medical-condition {
            padding: 2px 8px !important;
            color: #842029 !important;
            border: 1px solid #842029 !important;
            border-radius: 3px !important;
            font-size: 11px !important;
            font-weight: bold !important;
            display: inline-flex !important;
            align-items: center !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-conatiner {
            display: flex;
            flex-direction: column;
            min-height: 100vh;
            position: relative;
          }
          .page-break { page-break-before: always; top: 100px; }
          .address-container {
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
            font-size: 12.5px;
          }
          .patientDetails { margin-top: 0 !important; padding-top: 0 !important; font-size: 12px !important; }
          .content { flex-grow: 1; }
          .footer { bottom: 0; left: 0; width: 100%; }
          .footer::after { content: ""; display: block; height: 50px; }
          * { font-family: Arial, sans-serif !important; margin: 0; padding: 0; box-sizing: border-box; }
          .vertical21cm-print * { font-family: Arial, sans-serif !important; font-size: 13px !important; }
          .vertical21cm-print .left-label-style { width: 125px !important; font-size: 11px !important; }
          .vertical21cm-print .right-label-style { width: 125px !important; font-size: 11px !important; }
        }
      `,
      scanStyles: true,
      targetStyles: ["*"],
    });
  };

  const handlePDFDownload = () => {
    if (!htmlprintRef.current) return;
    html2pdf()
      .set({
        margin: isVertical21cm ? [75, 20, 20, 12] : [10, 10, 10, 10],
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 10, scrollY: 0, width: 794, minHeight: 1123 },
        filename: `${selectedOPD?.caseNumber}_${selectedOPD?.patientName}.pdf`,
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(htmlprintRef.current)
      .save();
  };

  const fetchReportCertificatePreview = async (description: string, opdItem: any) => {
    try {
      const formData = {
        consumerId: userContext?.consumerId,
        branchId: userContext?.branchId,
        organizationId: userContext?.organizationId,
        patientId: opdItem?.patientId || 0,
        opdId: opdItem?.opdId || 0,
        description: description?.toString() || "",
      };
      const res: any = await GetPatientReportCertificatePreview(formData);
      if (res?.data) {
        setReportPreviewData({
          previewDescription: res?.data?.previewDescription,
          medicalReportCertificateHeader: res?.data?.medicalReportCertificateHeader,
        });
        setReportZoomLevel(1);
        setReportPreviewOpen(true);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message || "Unable to load report",
        alertType: AlertType.Error,
      });
    }
  };

  const fetchReportCertificateInlineData = async (
    description: string,
    opdItem: any,
  ): Promise<{ previewDescription: any; medicalReportCertificateHeader: any } | null> => {
    try {
      const formData = {
        consumerId: userContext?.consumerId,
        branchId: userContext?.branchId,
        organizationId: userContext?.organizationId,
        patientId: opdItem?.patientId || 0,
        opdId: opdItem?.opdId || 0,
        description: description?.toString() || "",
      };
      const res: any = await GetPatientReportCertificatePreview(formData);
      if (res?.data) {
        return {
          previewDescription: res?.data?.previewDescription,
          medicalReportCertificateHeader: res?.data?.medicalReportCertificateHeader,
        };
      }
      return null;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message || "Unable to load report",
        alertType: AlertType.Error,
      });
      return null;
    }
  };

  const handleReportZoomIn = () => setReportZoomLevel((p) => Math.min(p + 0.25, 2));
  const handleReportZoomOut = () => setReportZoomLevel((p) => Math.max(p - 0.25, 0.25));

  const handleReportPrint = () => {
    if (!reportPrintRef.current) return;
    printHtmlContent(reportPrintRef.current, {
      printable: reportPrintRef.current.innerHTML,
      type: "raw-html",
      maxWidth: 1000,
      style: `
        @media print {
          body { margin: 0; padding: 0; background-color: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          * { font-family: Arial, sans-serif !important; margin: 0; padding: 0; box-sizing: border-box; }
        }
      `,
      scanStyles: true,
      targetStyles: ["*"],
    });
  };

  const handleReportDownload = () => {
    if (!reportPrintRef.current) return;
    html2pdf()
      .set({
        margin: [12.7, 20.32, 12.7, 20.32],
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 10, scrollY: 0, width: 794, minHeight: 1123 },
        filename: "report.pdf",
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      })
      .from(reportPrintRef.current)
      .save();
  };

  let urlParams = useQuery();
  let curRawUnitId = urlParams.get("q");
  let curOPDId = 0;
  let currentDetailOpdId = 0;
  if (curRawUnitId !== null && curRawUnitId !== undefined) {
    let decId = edService.decrypt(curRawUnitId);
    if (decId !== "" && decId !== null && decId !== undefined && +decId > 0) {
      curOPDId = +decId;
    } else {
      navigate("/not-found");
    }
  } else {
    navigate("/not-found");
  }

  const rawCurrentDetailOpdId = urlParams.get("currentDetailOpdId");
  if (rawCurrentDetailOpdId) {
    const decryptedCurrentDetailOpdId = edService.decrypt(rawCurrentDetailOpdId);
    if (
      decryptedCurrentDetailOpdId !== "" &&
      decryptedCurrentDetailOpdId !== null &&
      decryptedCurrentDetailOpdId !== undefined &&
      +decryptedCurrentDetailOpdId > 0
    ) {
      currentDetailOpdId = +decryptedCurrentDetailOpdId;
    }
  }

  const rawHistorySummaryOpdIds = urlParams.get("historySummaryOpdIds");
  let historySummaryOpdIds: number[] | null = null;
  if (rawHistorySummaryOpdIds) {
    const decryptedHistorySummaryOpdIds = edService.decryptToObject(rawHistorySummaryOpdIds);
    if (Array.isArray(decryptedHistorySummaryOpdIds)) {
      historySummaryOpdIds = decryptedHistorySummaryOpdIds
        .map((opdId: any) => Number(opdId))
        .filter((opdId: number) => opdId > 0);
    }
  }

  const buildParams = (fields: Record<string, any>) => {
    const params = new URLSearchParams();
    Object.entries(fields).forEach(([key, value]) => {
      params.append(key, value !== undefined && value !== null ? value.toString() : "");
    });
    return params;
  };

  const formatLastVisited = (opdItem: any): { dateStr: string; agoText: string } | null => {
    if (!opdItem?.lastVisitedPatient?.lastCaseDate) return null;
    const lastOpdDate = new Date(opdItem.lastVisitedPatient.lastCaseDate);
    const diff = lastOpdVisitTime(lastOpdDate);
    const day = String(lastOpdDate.getDate()).padStart(2, "0");
    const month = String(lastOpdDate.getMonth() + 1).padStart(2, "0");
    const dateStr = `${day}-${month}-${lastOpdDate.getFullYear()}`;
    const parts: string[] = [];
    if (diff.Year > 0) parts.push(`${diff.Year} year${diff.Year > 1 ? "s" : ""}`);
    if (diff.Month > 0) parts.push(`${diff.Month} month${diff.Month > 1 ? "s" : ""}`);
    if (diff.Day > 0) parts.push(`${diff.Day} day${diff.Day > 1 ? "s" : ""}`);
    return { dateStr, agoText: parts.length > 0 ? `${parts.join(", ")} ago` : "" };
  };

  const fetchChiefComplaint = async (opdItem: any) => {
    try {
      const params = buildParams({
        opdId: opdItem?.opdId,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        departmentId: opdItem?.departmentId,
        specialityId: opdItem?.specialityId,
      });
      const res: any = await GetOPDCheifComplaintsDetail(params);
      return res?.data?.data?.opdChiefComplaintDetailColl ?? [];
    } catch {
      return [];
    }
  };

  const fetchHistorySummary = async (opdItem: any) => {
    try {
      const params = buildParams({
        opdId: opdItem?.opdId,
        patientId: opdItem?.patientId,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        departmentId: opdItem?.departmentId,
        specialityId: opdItem?.specialityId,
      });
      const res: any = await GetPatientHistoryOfSummaryDetail(params);
      const item = res?.data?.data;
      return item
        ? [
          {
            historyTemplateName: item?.historyTemplateName || "",
            note: item?.note?.trim() || "",
            documentColl: buildDocumentFiles(item?.documentFileColl),
          },
        ]
        : [];
    } catch {
      return [];
    }
  };

  const fetchExaminationSummary = async (opdItem: any) => {
    try {
      const params = buildParams({
        opdId: opdItem?.opdId,
        patientId: opdItem?.patientId,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        departmentId: opdItem?.departmentId,
        specialityId: opdItem?.specialityId,
      });
      const res: any = await GetOPDExaminationSummaryDetail(params);
      const item = res?.data?.data;
      return item ? [item] : [];
    } catch {
      return [];
    }
  };

  const fetchInvestigation = async (opdItem: any) => {
    try {
      const params = buildParams({
        opdId: opdItem?.opdId,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        departmentId: opdItem?.departmentId,
        specialityId: opdItem?.specialityId,
        patientId: opdItem?.patientId,
        sortColumn: parameters.sortColumn,
        sortDirection: parameters.sortDirection,
        searchValue: parameters.searchValue,
      });
      const result: any = await GetOPDInvestigationDetailById(params);
      const item = result?.data?.data;
      const commonDocuments = buildDocumentFiles(item?.documentFileColl);
      const reportCertificateColl = item?.reportCertificateColl || [];
      const detailColl = Array.isArray(item?.opdInvestigationDetailColl)
        ? item.opdInvestigationDetailColl
          .filter((x: any) => x?.isDeleted !== true)
          .sort((a: any, b: any) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
          .map((x: any) => ({
            opdInvestigationDetailId: x?.opdInvestigationDetailId || 0,
            investigationId: x?.investigationId || 0,
            investigationName: x?.investigationName || "",
            note: x?.note?.trim() || "",
            isNoteEnabled: x?.isNoteEnabled || false,
            displayOrder: x?.displayOrder ?? 0,
            reportCertificateDetailColl: x?.reportCertificateDetailColl || [],
            documentColl: buildDocumentFiles(
              (item?.detailDocumentFileColl || []).filter(
                (doc: any) => doc?.linkId === x?.opdInvestigationDetailId,
              ),
            ),
          }))
        : [];
      return { detailColl, commonDocuments, reportCertificateColl };
    } catch {
      return { detailColl: [], commonDocuments: [], reportCertificateColl: [] };
    }
  };

  const fetchDiagnosis = async (opdItem: any) => {
    try {
      const params = buildParams({
        departmentId: opdItem?.departmentId,
        specialityId: opdItem?.specialityId,
        opdId: opdItem?.opdId,
        patientId: opdItem?.patientId,
        sortColumn: parameters.sortColumn,
        sortDirection: parameters.sortDirection,
        searchValue: parameters.searchValue,
      });
      const result: any = await GetOPDDiagnosisDetailById(params);
      const item = result?.data?.data;
      return Array.isArray(item?.opdDiagnosisDetailColl)
        ? item.opdDiagnosisDetailColl
          .filter((x: any) => x?.isDeleted !== true)
          .sort((a: any, b: any) => (a?.displayOrder ?? 0) - (b?.displayOrder ?? 0))
          .map((x: any) => ({
            opdDiagnosisDetailId: x?.opdDiagnosisDetailId || 0,
            diagnosisId: x?.diagnosisId || 0,
            diagnosisName: x?.diagnosisName || "",
            diagnosisTypeName: x?.diagnosisTypeName || "",
            diagnosisSeverityLevel: x?.diagnosisSeverityLevel || "",
            diagnosisSeverityName:
              diagnosisOptions.find((o) => o.value === x?.diagnosisSeverityLevel)?.label || "",
            displayOrder: x?.displayOrder ?? 0,
          }))
        : [];
    } catch {
      return [];
    }
  };

  const buildNotesFromOpdItem = (opdItem: any) => {
    const notesData: any[] = [];
    if (opdItem?.receptionistNote) {
      notesData.push({
        type: "Receptionist Note",
        note: opdItem.receptionistNote,
        repeat: opdItem?.receptionistNoteRepeat || "",
      });
    }
    if (opdItem?.personalNote) {
      notesData.push({
        type: "Personal Note",
        note: opdItem.personalNote,
        repeat: opdItem?.personalNoteRepeat || "",
      });
    }
    return notesData;
  };

  const loadOpdBundle = async (opdId: number): Promise<OpdBundle | null> => {
    if (!opdId || loadingOpdIdsRef.current.has(opdId)) return null;
    loadingOpdIdsRef.current.add(opdId);
    try {
      const detailParams = buildParams({
        opdId,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        userId: userContext?.loginDetail?.userId,
      });
      const detailRes: any = await GetOPDDetail(detailParams);
      const opdItem = detailRes?.data?.data;
      if (!opdItem) return null;

      const [
        chiefComplaintColl,
        historySummaryColl,
        examinationSummaryColl,
        investigationResult,
        diagnosisSummaryColl,
        reportData,
      ] = await Promise.all([
        fetchChiefComplaint(opdItem),
        fetchHistorySummary(opdItem),
        fetchExaminationSummary(opdItem),
        fetchInvestigation(opdItem),
        fetchDiagnosis(opdItem),
        fetchReportPayloadForPreview(opdItem),
      ]);

      const investigationSummaryColl = investigationResult?.detailColl || [];
      const investigationCommonDocuments = investigationResult?.commonDocuments || [];
      const investigationReportCertificateColl = investigationResult?.reportCertificateColl || [];
      const prescriptionSummaryColl = buildPrescriptionFromReport(reportData);
      const { bulletLines: generalInstructionColl, noteLines: generalInstructionNotes } =
        buildGeneralInstructionFromReport(reportData);
      const followUpColl = buildFollowUpFromReport(reportData);
      const doctorToBeReferredColl = buildDoctorReferredFromReport(reportData);
      const vaccinationColl = (reportData?.patientVaccinations || []).filter(
        (v: any) => v?.brandName || v?.genericName || v?.doseName,
      );

      const bundle: OpdBundle = {
        opd: opdItem,
        chiefComplaintColl,
        historySummaryColl,
        examinationSummaryColl,
        investigationSummaryColl,
        investigationCommonDocuments,
        investigationReportCertificateColl,
        diagnosisSummaryColl,
        prescriptionSummaryColl,
        generalInstructionColl,
        generalInstructionNotes,
        followUpColl,
        doctorToBeReferredColl,
        notesColl: buildNotesFromOpdItem(opdItem),
        vaccinationColl,
        reportData,
      };
      return bundle;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message || "Unable to load OPD details",
        alertType: AlertType.Error,
      });
      return null;
    } finally {
      loadingOpdIdsRef.current.delete(opdId);
    }
  };

  useEffect(() => {
    if (curOPDId > 0) {
      (async () => {
        setIsInitialLoading(true);
        const bundle = await loadOpdBundle(curOPDId);
        if (bundle) {
          setOpdBundles([bundle]);
          setActiveOpdId(bundle.opd.opdId);
          setNoMoreOpds(false);
        }
        setIsInitialLoading(false);
      })();
    }
  }, [curOPDId]);

  const getOPDHistoryByPatientId = async (patientId: string) => {
    setIsOpdHistoryLoading(true);
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("patientIds", patientId);
      params.append("pageIndex", "1");
      params.append("pageSize", OPD_HISTORY_PAGE_SIZE.toString());
      params.append("sortColumn", "case_date");
      params.append("sortDirection", "DESC");
      params.append("searchValue", "");
      params.append("tabType", "COMPLETE");
      params.append("exactMatch", "false");

      const res: any = await GetOPDList(params);
      if (res?.data?.data?.opdColl) {
        const seenOpdIds = new Set<number>();
        const historyColl = [...res.data.data.opdColl]
          .filter((item: any) => item?.caseStatus === "COMPLETE")
          .sort((a: any, b: any) => {
            const aTime = a?.caseDate ? new Date(a.caseDate).getTime() : 0;
            const bTime = b?.caseDate ? new Date(b.caseDate).getTime() : 0;
            return bTime - aTime;
          })
          .filter((item: any) => {
            if (!item?.opdId) return true;
            if (seenOpdIds.has(item.opdId)) return false;
            seenOpdIds.add(item.opdId);
            return true;
          })
          .filter((item: any) =>
            historySummaryOpdIds
              ? historySummaryOpdIds.includes(Number(item?.opdId))
              : !currentDetailOpdId || item?.opdId !== currentDetailOpdId,
          )
          .sort((a: any, b: any) => {
            if (!historySummaryOpdIds) return 0;
            return (
              historySummaryOpdIds.indexOf(Number(a?.opdId)) -
              historySummaryOpdIds.indexOf(Number(b?.opdId))
            );
          });
        setOpdHistoryColl(historyColl);
      } else {
        setOpdHistoryColl([]);
      }
    } catch (e: any) {
      setOpdHistoryColl([]);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message || "Unable to get OPD History",
        alertType: AlertType.Error,
      });
    } finally {
      setIsOpdHistoryLoading(false);
    }
  };

  const firstBundlePatientId = opdBundles[0]?.opd?.patientId;
  useEffect(() => {
    if (firstBundlePatientId) {
      getOPDHistoryByPatientId(firstBundlePatientId.toString());
    }
  }, [firstBundlePatientId, currentDetailOpdId, rawHistorySummaryOpdIds]);

  useEffect(() => {
    if (OpdHistoryColl.length > 0 && opdBundles.length > 0) {
      const idx = OpdHistoryColl.findIndex((o: any) => o?.opdId === opdBundles[0]?.opd?.opdId);
      if (idx >= 0) setSequenceStartIndex(idx);
    }
  }, [OpdHistoryColl]);

  const loadNextOpdInSequence = async () => {
    if (loadNextGuardRef.current) return;
    if (noMoreOpds) return;
    if (OpdHistoryColl.length === 0 || opdBundles.length === 0) return;
    const loadedIds = new Set(opdBundles.map((b) => b.opd.opdId));
    const lastLoadedOpdId = opdBundles[opdBundles.length - 1]?.opd?.opdId;
    let idx = OpdHistoryColl.findIndex((o: any) => o?.opdId === lastLoadedOpdId);
    if (idx < 0) idx = sequenceStartIndex;
    let nextIndex = idx + 1;
    while (nextIndex < OpdHistoryColl.length) {
      const candidateId = OpdHistoryColl[nextIndex]?.opdId;
      if (candidateId && !loadedIds.has(candidateId) && !failedOpdIdsRef.current.has(candidateId)) break;
      nextIndex++;
    }

    if (nextIndex >= OpdHistoryColl.length) {
      setNoMoreOpds(true);
      return;
    }
    const nextOpdId = OpdHistoryColl[nextIndex]?.opdId;
    if (!nextOpdId) return;

    loadNextGuardRef.current = true;
    setIsLoadingNextOpd(true);
    try {
      const bundle = await loadOpdBundle(nextOpdId);
      if (bundle) {
        setOpdBundles((prev) => (prev.some((b) => b.opd.opdId === bundle.opd.opdId) ? prev : [...prev, bundle]));
      } else if (!loadingOpdIdsRef.current.has(nextOpdId)) {
        failedOpdIdsRef.current.add(nextOpdId);
      }
    } finally {
      setIsLoadingNextOpd(false);
      loadNextGuardRef.current = false;
    }
  };

  useEffect(() => {
    const el = bottomSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadNextOpdInSequence();
        }
      },
      { rootMargin: "800px 0px 800px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [opdBundles, sequenceStartIndex, OpdHistoryColl, noMoreOpds, isLoadingNextOpd]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = Number(entry.target.getAttribute("data-opd-id"));
            if (id) setActiveOpdId((prev) => (prev === id ? prev : id));
          }
        });
      },
      { root: null, rootMargin: `-${DEFAULT_HEADER_HEIGHT + STICKY_TOP_OFFSET + 40}px 0px -65% 0px`, threshold: 0 },
    );
    Object.values(opdBlockRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [opdBundles]);

  useEffect(() => {
    if (!activeOpdId) return;
    const container = historyListRef.current;
    const item = historyItemRefs.current[activeOpdId];
    if (!container || !item) return;

    const cRect = container.getBoundingClientRect();
    const iRect = item.getBoundingClientRect();
    const PAD = 8;

    if (iRect.top < cRect.top + PAD) {
      container.scrollTop += iRect.top - cRect.top - PAD;
    } else if (iRect.bottom > cRect.bottom - PAD) {
      container.scrollTop += iRect.bottom - cRect.bottom + PAD;
    }
  }, [activeOpdId, OpdHistoryColl]);

  const scrollToOpdBlock = (opdId: number) => {
    const el = opdBlockRefs.current[opdId];
    if (el) {
      const headerOffset = (opdHeaderHeights[opdId] || DEFAULT_HEADER_HEIGHT) + CLOSE_BAR_HEIGHT + STICKY_TOP_OFFSET + 16;
      const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleSelectOpdFromHistory = async (clickedOpdId: number) => {
    if (!clickedOpdId || clickedOpdId === activeOpdId) return;

    const alreadyLoaded = opdBundles.some((b) => b.opd.opdId === clickedOpdId);
    if (alreadyLoaded) {
      setActiveOpdId(clickedOpdId);
      scrollToOpdBlock(clickedOpdId);
      return;
    }

    setIsLoadingNextOpd(true);
    const bundle = await loadOpdBundle(clickedOpdId);
    setIsLoadingNextOpd(false);
    if (!bundle) return;

    const idx = OpdHistoryColl.findIndex((o: any) => o?.opdId === clickedOpdId);
    setSequenceStartIndex(idx >= 0 ? idx : 0);
    setNoMoreOpds(false);
    failedOpdIdsRef.current.clear();
    pendingScrollOpdIdRef.current = clickedOpdId;
    setOpdBundles([bundle]);
    setActiveOpdId(clickedOpdId);
  };

  useLayoutEffect(() => {
    const targetOpdId = pendingScrollOpdIdRef.current;
    if (!targetOpdId) return;
    if (opdBundles.length !== 1 || opdBundles[0]?.opd?.opdId !== targetOpdId) return;
    pendingScrollOpdIdRef.current = null;
    const el = opdBlockRefs.current[targetOpdId];
    if (el) {
      const headerOffset = (opdHeaderHeights[targetOpdId] || DEFAULT_HEADER_HEIGHT) + CLOSE_BAR_HEIGHT + STICKY_TOP_OFFSET + 16;
      const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top: Math.max(top, 0), behavior: "auto" });
    }
  }, [opdBundles, opdHeaderHeights]);

  const scrollToSection = (key: PreviewTabKey) => {
    setActiveTab(key);
    const opdId = activeOpdId || opdBundles[0]?.opd?.opdId;
    const target = opdId ? sectionRefsByOpd.current[opdId]?.[key] : undefined;
    if (target) {
      const headerOffset = (opdHeaderHeights[opdId] || DEFAULT_HEADER_HEIGHT) + CLOSE_BAR_HEIGHT + STICKY_TOP_OFFSET + 16;
      const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleWindowScroll = useCallback(() => {
    if (scrollTickingRef.current) return;
    scrollTickingRef.current = true;
    requestAnimationFrame(() => {
      scrollTickingRef.current = false;
      const opdId = activeOpdId || opdBundles[0]?.opd?.opdId;
      if (!opdId) return;
      const refsForOpd = sectionRefsByOpd.current[opdId];
      if (!refsForOpd) return;
      const headerOffset = (opdHeaderHeights[opdId] || DEFAULT_HEADER_HEIGHT) + CLOSE_BAR_HEIGHT + STICKY_TOP_OFFSET + 24;
      let current: PreviewTabKey = PREVIEW_TABS[0].key;
      for (const tab of PREVIEW_TABS) {
        const el = refsForOpd[tab.key];
        if (el && el.getBoundingClientRect().top - headerOffset <= 0) {
          current = tab.key;
        }
      }
      setActiveTab((prev) => (prev === current ? prev : current));
    });
  }, [opdHeaderHeights, activeOpdId, opdBundles]);

  useEffect(() => {
    window.addEventListener("scroll", handleWindowScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleWindowScroll);
  }, [handleWindowScroll]);

  useEffect(() => {
    return () => {
      opdBundlesRef.current.forEach((b) => {
        const files: any[] = [
          ...(b.investigationCommonDocuments || []),
          ...(b.historySummaryColl || []).flatMap((h: any) => h?.documentColl || []),
          ...(b.investigationSummaryColl || []).flatMap((i: any) => i?.documentColl || []),
        ];
        files.forEach((f: any) => {
          if (f?.preview) {
            try {
              URL.revokeObjectURL(f.preview);
            } catch { }
          }
        });
      });
    };
  }, []);

  const activeBundleForNav = opdBundles.find((b) => b.opd?.opdId === activeOpdId) || opdBundles[0];
  const activeHasVaccination =
    activeBundleForNav?.opd?.enableVaccination === true &&
    vaccinationEnableMode === "TRUE" &&
    (activeBundleForNav?.vaccinationColl?.length || 0) > 0;

  const handleClosePreview = () => {
    navigate(-1);
  };

  const renderOpdHistoryListBody = (closeOnSelect: boolean = false) => {
    if (isOpdHistoryLoading) {
      return null;
    }
    if (!OpdHistoryColl || OpdHistoryColl.length === 0) {
      return (
        <Typography sx={{ fontSize: "13px", color: "#777", padding: "16px" }}>
          No OPD History available
        </Typography>
      );
    }
    return (
      <List dense disablePadding>
        {OpdHistoryColl.map((item: any) => (
          <ListItemButton
            key={item?.opdId}
            ref={
              closeOnSelect
                ? undefined
                : (el: HTMLDivElement | null) => {
                  if (item?.opdId) historyItemRefs.current[item.opdId] = el;
                }
            }
            selected={activeOpdId === item?.opdId}
            onClick={async () => {
              if (!item?.opdId) return;
              await handleSelectOpdFromHistory(item.opdId);
              if (closeOnSelect) setMobileHistoryOpen(false);
            }}
            sx={{
              display: "block",
              borderLeft: "3px solid transparent",
              borderBottom: "1px solid #0000000f",
              padding: "8px 12px",
              "&.Mui-selected": {
                borderLeft: "3px solid #1976d2",
                backgroundColor: "var(--opd-header-bg)",
              },
              "&.Mui-selected:hover": {
                backgroundColor: "var(--opd-header-bg)",
              },
            }}
          >
            <ListItemText
              primaryTypographyProps={{ fontSize: "12px", fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: "12px", color: "#555" }}
              primary={`OPD No: ${item?.caseNumber || "-"}`}
              secondary={<>Date: {item?.caseDate || ""}</>}
            />
          </ListItemButton>
        ))}
      </List>
    );
  };

  const renderOpdBlock = (bundle: OpdBundle) => {
    const opdItem = bundle.opd;
    const opdId = opdItem?.opdId;
    const setSectionRef = (key: PreviewTabKey) => (el: HTMLDivElement | null) => {
      if (!sectionRefsByOpd.current[opdId]) sectionRefsByOpd.current[opdId] = {};
      sectionRefsByOpd.current[opdId][key] = el;
    };

    const lastVisited = formatLastVisited(opdItem);
    const opdOwnDateTime = formatOpdOwnDateTime(opdItem);

    const consultingDoctorChipSx = {
      display: "inline-flex",
      alignItems: "center",
      px: 1.5,
      py: 0.25,
      borderRadius: "4px",
      backgroundColor: "#fff3cd",
      border: "1px solid #f0d073",
      color: "#664d03",
      fontSize: "12px",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.02em",
      whiteSpace: "nowrap",
    };

    const hasVitals =
      opdItem?.weight > 0 ||
      opdItem?.height > 0 ||
      opdItem?.bmi > 0 ||
      opdItem?.bsa > 0 ||
      opdItem?.pulseRate > 0 ||
      opdItem?.respiratoryRate > 0 ||
      (opdItem?.bpDisplayValue && opdItem?.bpDisplayValue !== "/") ||
      opdItem?.spo2Amount > 0 ||
      opdItem?.temperatureAmount > 0 ||
      opdItem?.rbs > 0 ||
      opdItem?.headCircumference > 0 ||
      opdItem?.motherHeight > 0 ||
      opdItem?.fatherHeight > 0;
    const vaccinationList = bundle.vaccinationColl || [];
    const showVaccinationSection =
      opdItem?.enableVaccination === true && vaccinationEnableMode === "TRUE" && vaccinationList.length > 0;
    const hasChiefComplaint = bundle.chiefComplaintColl?.length > 0;
    const hasHistory =
      bundle.historySummaryColl?.length > 0 &&
      bundle.historySummaryColl.some(
        (item: any) => item?.historyTemplateName || item?.note || item?.documentColl?.length > 0,
      );
    const hasExamination =
      bundle.examinationSummaryColl?.length > 0 && bundle.examinationSummaryColl.some((item: any) => item?.note);
    const hasInvestigation =
      bundle.investigationSummaryColl?.length > 0 ||
      bundle.investigationCommonDocuments?.length > 0 ||
      bundle.investigationReportCertificateColl?.length > 0;
    const hasDiagnosis = bundle.diagnosisSummaryColl?.length > 0;
    const hasPrescription = bundle.prescriptionSummaryColl?.length > 0;
    const hasGeneralInstruction =
      bundle.generalInstructionColl?.length > 0 || bundle.generalInstructionNotes?.length > 0;
    const hasFollowUp =
      bundle.followUpColl?.length > 0 &&
      bundle.followUpColl.some(
        (item: any) => item?.summaryLine || item?.instructionTexts?.length > 0 || item?.notes?.length > 0,
      );
    const hasDoctorReferred = bundle.doctorToBeReferredColl?.length > 0;
    const hasNotes = bundle.notesColl?.length > 0;

    return (
      <Box
        key={opdId}
        ref={(el: HTMLDivElement | null) => (opdBlockRefs.current[opdId] = el)}
        data-opd-id={opdId}
        sx={{
          display: "flex",
          flexDirection: "column",
          border: "1px solid #0000001f",
          borderRadius: "8px",
          backgroundColor: "var(--opd-panel-bg)",
        }}
      >

        <Box
          ref={(el: HTMLDivElement | null) => (opdHeaderRefs.current[opdId] = el)}
          sx={{
            position: "sticky",
            top: CLOSE_BAR_HEIGHT,
            zIndex: (t) => t.zIndex.appBar,
            backgroundColor: "var(--opd-panel-bg)",
            borderBottom: "1px solid #0000001f",
            borderRadius: "8px 8px 0 0",
          }}
        >

          <Box sx={{ backgroundColor: "var(--opd-header-bg)", padding: "8px 16px", borderRadius: "8px 8px 0 0" }}>
            {/* MOBILE SCREEN ONLY */}
            {opdItem?.consultingDoctorName && (
              <Box
                sx={{
                  display: { xs: "flex", sm: "none" },
                  justifyContent: "flex-end",
                  marginBottom: "4px",
                }}
              >
                <Box sx={consultingDoctorChipSx}>{opdItem.consultingDoctorName}</Box>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <span className="patient-container" style={{ color: "black" }}>
                {opdItem?.patientNumber ? `#${opdItem.patientNumber} - ` : ""}{" "}
                {opdItem?.patientName}{" "}
              </span>
              <span className="text-saperator">|</span>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: "bold",
                  color:
                    opdItem?.patientGender === "MALE"
                      ? "#2e67a3"
                      : opdItem?.patientGender === "FEMALE"
                        ? "#BC3965"
                        : "black",
                }}
              >
                {opdItem?.patientGender === "MALE" ? "M" : opdItem?.patientGender === "FEMALE" ? "F" : "O"}
              </span>
              <span style={{ fontSize: 16, fontWeight: "bold", color: "#414D56" }}>
                {opdItem?.ageYear > 0 ? ` ${opdItem.ageYear}y` : ""}
                {opdItem?.ageMonth > 0 ? ` ${opdItem.ageMonth}m` : ""}
                {opdItem?.ageDay > 0 ? ` ${opdItem.ageDay}d` : ""}
              </span>
              {(opdItem?.patientArea || opdItem?.patientCity) && (
                <>
                  <span className="text-saperator">|</span>
                  <span className="detail-header-icon-container">
                    <LocationOnRoundedIcon className="detail-header-icon" />
                    {opdItem?.patientArea}
                    {opdItem?.patientCity}
                  </span>
                </>
              )}
              {opdItem?.consultingDoctorName && (
                <Box
                  sx={{
                    ...consultingDoctorChipSx,
                    display: { xs: "none", sm: "inline-flex" },
                    marginLeft: "auto",
                  }}
                >
                  {opdItem.consultingDoctorName}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 0, sm: 2 },
                padding: "6px 0px 2px",
                alignItems: "center",
              }}
            >

              <span className="bold-600" style={{ color: "black" }}>
                {opdItem?.caseNumber}
              </span>
              <span className="text-saperator">|</span>
              {opdOwnDateTime && (
                <Typography className="bold-600" style={{ color: "black" }}>
                  {opdOwnDateTime}
                </Typography>
              )}
              {(opdItem?.referenceByDoctorName ||
                opdItem?.referralPatientName ||
                opdItem?.referredByName ||
                opdItem?.referredByCodeLabel) && (
                  <Typography className="bold-600" style={{ color: "black" }}>
                    Ref. By{" "}
                    {opdItem?.referenceByDoctorName ||
                      opdItem?.referralPatientName ||
                      opdItem?.referredByName ||
                      opdItem?.referredByCodeLabel}
                  </Typography>
                )}
              {/* ── PRINT ICON + TEMPLATE CHEVRON. ── */}
              <Box
                sx={{
                  display: "flex",
                  gap: 0,
                  alignItems: "center",
                  marginLeft: "auto",
                }}
              >
                <Tooltip
                  title={`Print OPD ${opdItem?.caseNumber || ""}`}
                  placement="top"
                >
                  <Box
                    onClick={() => handlePrintOpd(bundle)}
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "4px 8px",
                      cursor: "pointer",
                      backgroundColor: "var(--opd-panel-bg)",

                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <PrintOutlinedIcon />
                  </Box>
                </Tooltip>
                <Tooltip title="Select Prescription Template" placement="top">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenTemplateSelector(bundle)}
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "4px",
                      backgroundColor: "var(--opd-panel-bg)",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 16px",
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
              {hasVitals && (
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                  {opdItem?.weight > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>WT </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.weight} Kg</span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.height > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>HT/LN </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.height} cm</span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.bmi > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>BMI </span>
                      <span style={{ color: "black", fontWeight: 500 }}>
                        {opdItem.bmi} kg/m<sup>2</sup>
                      </span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.bsa > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>BSA </span>
                      <span style={{ color: "black", fontWeight: 500 }}>
                        {opdItem.bsa} m<sup>2</sup>
                      </span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.pulseRate > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>PR </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.pulseRate} bpm</span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.respiratoryRate > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>RR </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.respiratoryRate} /min</span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.bpDisplayValue && opdItem.bpDisplayValue !== "/" && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>BP </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.bpDisplayValue} mmHg</span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.spo2Amount > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>SpO2 </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.spo2Amount} %</span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.temperatureAmount > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>TEMP </span>
                      <span style={{ color: "black", fontWeight: 500 }}>
                        {opdItem.temperatureAmount} {getLabel(opdItem.temperatureUnit, temperatureOptions)}
                      </span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.rbs > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>RBS </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.rbs} mg/dl</span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.headCircumference > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>HC </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.headCircumference} cm</span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.motherHeight > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>MH </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.motherHeight} cm</span>
                      <span className="text-saperator">|</span>
                    </Box>
                  )}
                  {opdItem?.fatherHeight > 0 && (
                    <Box>
                      <span style={{ color: "#74788d", fontSize: "12px" }}>FH </span>
                      <span style={{ color: "black", fontWeight: 500 }}>{opdItem.fatherHeight} cm</span>
                    </Box>
                  )}
                </Box>
              )}

              {opdItem?.specialMedicalConditionColl?.length > 0 && (
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ margin: "2px 0px", marginLeft: hasVitals ? 1 : 0 }}>
                  {opdItem.specialMedicalConditionColl
                    .filter((item: any) => item?.medicalConditionName)
                    .map((item: any, index: number) => (
                      <div key={index} className="custom-danger-alert bold-600">
                        {item.medicalConditionName}
                      </div>
                    ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {hasChiefComplaint && (
          <Box ref={setSectionRef("CHIEF_COMPLAINT")}>
            <SectionRow title="Chief Complaint Summary">
              <Box sx={{ paddingLeft: "17px" }}>
                {bundle.chiefComplaintColl.map((item: any, idx: number) => (
                  <li key={idx}>
                    <strong style={{ fontSize: "14px" }}>{item?.symptomName}</strong>
                    <div style={{ paddingLeft: "17px" }}>
                      {[
                        (item?.durationValue || 0) > 0
                          ? (item?.durationValue || "") +
                          (item?.durationUnitName ? " " + item?.durationUnitName : "")
                          : "",
                        item?.severityName || "",
                        (item?.frequencyValue || 0) > 0
                          ? (item?.frequencyValue || "") +
                          (item?.frequencyUnitName ? " " + item?.frequencyUnitName : "")
                          : "",
                        item?.natureName || "",
                        item?.timingName || "",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </div>

                    {item.opdChiefComplaintAgravatedByColl?.some(
                      (x: any) => x.isDeleted === false && x.mode !== "D",
                    ) && (
                        <div style={{ paddingLeft: "17px" }}>
                          <span>Aggravated By:</span>{" "}
                          {item.opdChiefComplaintAgravatedByColl
                            .filter((x: any) => x.isDeleted === false && x.mode !== "D")
                            .map((x: any) => x.agravatedByName)
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}

                    {item.opdChiefComplaintRelievedByColl?.some(
                      (x: any) => x.isDeleted === false && x.mode !== "D",
                    ) && (
                        <div style={{ paddingLeft: "17px" }}>
                          <span>Relieved By:</span>{" "}
                          {item.opdChiefComplaintRelievedByColl
                            .filter((x: any) => x.isDeleted === false && x.mode !== "D")
                            .map((x: any) => x.relievedByName)
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}

                    {item?.note && (
                      <div style={{ paddingLeft: "17px", marginBottom: "8px" }}>
                        {stripHtmlTags(item.note)}
                      </div>
                    )}
                  </li>
                ))}
              </Box>
            </SectionRow>
          </Box>
        )}

        {/* History Summary */}
        {hasHistory && (
          <Box ref={setSectionRef("HISTORY")}>
            <SectionRow title="History Summary">
              {bundle.historySummaryColl.map((item: any, idx: number) => (
                <Box key={idx} sx={{ paddingLeft: "17px" }}>
                  {item?.note && (
                    <Box sx={{ fontSize: "14px", lineHeight: 1.6 }}>{stripHtmlTags(item.note)}</Box>
                  )}
                  {/* Attached documents for History Summary */}
                  <DocumentList documents={item?.documentColl} />
                </Box>
              ))}
            </SectionRow>
          </Box>
        )}

        {hasExamination && (
          <Box ref={setSectionRef("EXAMINATION")}>
            <SectionRow title="Examination Summary">
              {bundle.examinationSummaryColl.map((item: any, idx: number) => (
                <Box key={idx} sx={{ paddingLeft: "17px" }}>
                  {item?.note && (
                    <Box sx={{ marginBottom: "10px" }}>
                      <Typography sx={{ fontSize: "14px", lineHeight: 1.6 }}>{stripHtmlTags(item.note)}</Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </SectionRow>
          </Box>
        )}

        {showVaccinationSection && (
          <Box ref={setSectionRef("VACCINATION")}>
            <SectionRow
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <VaccinesIcon sx={{ fontSize: "16px", color: "#1976d2" }} />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "13px",
                      color: "#555",
                      fontWeight: 600,
                    }}
                  >
                    Vaccines Given
                  </Typography>
                </Box>
              }
            >
              {vaccinationList.map((item: any, index: number) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "14px", fontWeight: 600, paddingLeft: "17px", marginBottom: "4px" }}
                >
                  {item?.brandName}
                  {item?.genericName ? ` (${item.genericName})` : ""} {item?.doseName}
                </Typography>
              ))}
            </SectionRow>
          </Box>
        )}

        {hasInvestigation && (
          <Box ref={setSectionRef("INVESTIGATION")}>
            <SectionRow title="Investigation Summary">
              <Box component="ul" sx={{ margin: 0, paddingLeft: "25px" }}>
                {bundle.investigationSummaryColl.map((item: any, idx: number) => (
                  <Box component="li" key={item?.opdInvestigationDetailId || idx} sx={{ marginBottom: "12px" }}>
                    {item?.investigationName && (
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, lineHeight: 1.5 }}>
                        {item.investigationName}
                      </Typography>
                    )}
                    {item?.note && (
                      <Typography sx={{ fontSize: "13px", paddingLeft: "17px", lineHeight: 1.6, marginTop: "2px" }}>
                        {stripHtmlTags(item.note)}
                      </Typography>
                    )}
                    <Box sx={{ paddingLeft: "17px" }}>
                      <DocumentList documents={item?.documentColl} />
                      <ReportCertificateList
                        certificates={item?.reportCertificateDetailColl}
                        onFetchData={(description) => fetchReportCertificateInlineData(description, opdItem)}
                        headerSections={certificateHeaderSelectedSections}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
              {bundle.investigationCommonDocuments?.length > 0 && (
                <Box sx={{ paddingLeft: "25px", marginTop: "4px" }}>
                  <DocumentList documents={bundle.investigationCommonDocuments} />
                </Box>
              )}
              {bundle.investigationReportCertificateColl?.length > 0 && (
                <Box sx={{ paddingLeft: "25px", marginTop: "4px" }}>
                  <ReportCertificateList
                    certificates={bundle.investigationReportCertificateColl}
                    onFetchData={(description) => fetchReportCertificateInlineData(description, opdItem)}
                    headerSections={certificateHeaderSelectedSections}
                  />
                </Box>
              )}
            </SectionRow>
          </Box>
        )}

        {/* Diagnosis Summary */}
        {hasDiagnosis && (
          <Box ref={setSectionRef("DIAGNOSIS")}>
            <SectionRow title="Diagnosis Summary">
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {bundle.diagnosisSummaryColl.map((item: any, idx: number) => {
                  const chipTooltip = [item?.diagnosisTypeName, item?.diagnosisSeverityName]
                    .filter(Boolean)
                    .join(" • ");
                  const severityStyle = getDiagnosisSeverityStyle(item?.diagnosisSeverityLevel);
                  return (
                    <Tooltip
                      key={item?.opdDiagnosisDetailId || idx}
                      title={chipTooltip}
                      placement="top"
                      disableHoverListener={!chipTooltip}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "5px 14px",
                          borderRadius: "4px",
                          backgroundColor: severityStyle.bg,
                          border: `1.5px solid ${severityStyle.border}`,
                          color: severityStyle.color,
                          fontSize: "12px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          lineHeight: 1.4,
                        }}
                      >
                        {item?.diagnosisName}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </SectionRow>
          </Box>
        )}

        {/* ── Prescription Summary ── */}
        {hasPrescription && (
          <Box ref={setSectionRef("PRESCRIPTION")}>
            <SectionRow title="Prescription Summary">
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {bundle.prescriptionSummaryColl.map((item: any, idx: number) => (
                  <Box
                    key={item?.opdPrescriptionDetailId || idx}
                    sx={{
                      display: "flex",
                      gap: "10px",
                      paddingBottom: "12px",
                      marginBottom: "12px",
                      borderBottom:
                        idx !== bundle.prescriptionSummaryColl.length - 1 ? "1px dashed #0000001f" : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, minWidth: "18px" }}>
                      {idx + 1}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            lineHeight: 1.5,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {item.medicineTitle}
                        </Typography>
                        {item?.quantity && (
                          <Typography sx={{ fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap" }}>
                            {item.quantity}
                            {item?.quantityUnit ? ` ${item.quantityUnit}` : ""}
                          </Typography>
                        )}
                      </Box>

                      {item.doseLines?.map((line: string, lIdx: number) => (
                        <Typography key={lIdx} sx={{ fontSize: "13px", lineHeight: 1.5 }}>
                          {line}
                        </Typography>
                      ))}

                      {item.route && (
                        <Typography sx={{ fontSize: "13px", lineHeight: 1.5, color: "#444" }}>
                          {item.route}
                        </Typography>
                      )}

                      {item.instructions?.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {item.instructions.map((text: string, mIdx: number) => (
                            <Typography key={mIdx} sx={{ fontSize: "13px", lineHeight: 1.6 }}>
                              {text}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </SectionRow>
          </Box>
        )}

        {/* ── General Instruction ── */}
        {hasGeneralInstruction && (
          <SectionRow title="General Instruction">
            {bundle.generalInstructionColl.map((text: string, idx: number) => (
              <Box key={`gi-${idx}`} sx={{ display: "flex", gap: "8px", marginBottom: "8px", paddingLeft: "5px" }}>
                <Typography sx={{ fontSize: "14px" }}>•</Typography>
                <Typography sx={{ fontSize: "14px", lineHeight: 1.6 }}>{text}</Typography>
              </Box>
            ))}
            {bundle.generalInstructionNotes.map((text: string, idx: number) => (
              <Typography
                key={`gin-${idx}`}
                sx={{ fontSize: "14px", lineHeight: 1.6, paddingLeft: "5px", marginBottom: "4px" }}
              >
                {text}
              </Typography>
            ))}
          </SectionRow>
        )}

        {/* ── Follow Up ── */}
        {hasFollowUp && (
          <Box ref={setSectionRef("FOLLOW_UP")}>
            <SectionRow title="Follow Up">
              {bundle.followUpColl.map((item: any, idx: number) => (
                <Box
                  key={item?.opdFollowUpId || idx}
                  sx={{ marginBottom: idx !== bundle.followUpColl.length - 1 ? "12px" : 0 }}
                >
                  {item.summaryLine && (
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, lineHeight: 1.6, marginBottom: "8px" }}>
                      {item.summaryLine}
                    </Typography>
                  )}

                  {(item.instructionTexts || []).map((text: string, tIdx: number) => (
                    <Box key={tIdx} sx={{ display: "flex", gap: "8px", marginBottom: "6px", paddingLeft: "5px" }}>
                      <Typography sx={{ fontSize: "14px" }}>•</Typography>
                      <Typography sx={{ fontSize: "14px", lineHeight: 1.6 }}>{text}</Typography>
                    </Box>
                  ))}

                  {(item.notes || []).length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 0.5 }}>Note:</Typography>
                      {item.notes.map((n: string, nIdx: number) => (
                        <Typography key={nIdx} sx={{ fontSize: "14px", lineHeight: 1.6 }}>
                          {n}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </SectionRow>
          </Box>
        )}

        {/* ── Doctors To Be Referred ── */}
        {hasDoctorReferred && (
          <Box ref={setSectionRef("DOCTOR_REFERRED")}>
            <SectionRow title="Doctor To Be Referred">
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {bundle.doctorToBeReferredColl.map((item: any, idx: number) => (
                  <Box
                    key={item?.opdDoctorToBeReferredId || idx}
                    sx={{
                      display: "flex",
                      gap: "10px",
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom:
                        idx !== bundle.doctorToBeReferredColl.length - 1 ? "1px dashed #0000001f" : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, minWidth: "18px" }}>
                      {idx + 1}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, lineHeight: 1.5 }}>
                        {item.name}
                        {item.qualification ? ` (${item.qualification})` : ""}
                      </Typography>
                      {item.hospitalName && (
                        <Typography sx={{ fontSize: "13px", color: "#666", lineHeight: 1.5 }}>
                          {item.hospitalName}
                        </Typography>
                      )}
                      {item.addressLine && (
                        <Typography
                          sx={{ fontSize: "13px", lineHeight: 1.5, whiteSpace: "normal", overflowWrap: "anywhere" }}
                        >
                          {item.addressLine}
                        </Typography>
                      )}
                      {item.contactLine && (
                        <Typography sx={{ fontSize: "13px", lineHeight: 1.5 }}>{item.contactLine}</Typography>
                      )}
                      {item.clinicalNote && (
                        <Typography
                          sx={{ fontSize: "13px", lineHeight: 1.6, whiteSpace: "normal", overflowWrap: "anywhere" }}
                        >
                          Clinical Note: {item.clinicalNote}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </SectionRow>
          </Box>
        )}

        {hasNotes && (
          <Box ref={setSectionRef("NOTES")}>
            <SectionRow title="Notes">
              {bundle.notesColl.map((item: any, idx: number) => (
                <Box key={idx} sx={{ marginBottom: idx !== bundle.notesColl.length - 1 ? "14px" : 0 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, marginBottom: "4px" }}>
                    {item?.type}
                  </Typography>
                  <Typography sx={{ fontSize: "14px", lineHeight: 1.6, paddingLeft: "5px" }}>
                    {stripHtmlTags(item?.note)}
                  </Typography>
                  {item?.repeat && (
                    <Typography sx={{ fontSize: "13px", color: "#666", marginTop: "4px", paddingLeft: "5px" }}>
                      Repeat: {item.repeat}
                    </Typography>
                  )}
                </Box>
              ))}
            </SectionRow>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={1}
        sx={{
          top: 0,
          zIndex: (t) => t.zIndex.appBar + 1,
          backgroundColor: "#ffffff !important",
          color: "#000000",
        }}
      >
        <Backdrop
          sx={{
            color: "#fff",
            backgroundColor: "var(--opd-panel-bg)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={showFullPageLoader}
        >
          <ProgressBar />
        </Backdrop>
        <Toolbar
          variant="dense"
          sx={{
            minHeight: `${CLOSE_BAR_HEIGHT}px`,
            height: `${CLOSE_BAR_HEIGHT}px`,
            width: "100%",
            maxWidth: 1400,
            mx: "auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => setMobileHistoryOpen(true)}
              size="small"
              sx={{ display: { xs: "inline-flex", md: "none" } }}
              aria-label="Open OPD History"
            >
              <MenuIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>Patient History</Typography>
          </Box>
          <Tooltip title="Close" placement="left">
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Dialog
        fullScreen
        open={mobileHistoryOpen}
        onClose={() => setMobileHistoryOpen(false)}
        sx={{ display: { xs: "block", md: "none" } }}
      >
        <AppBar position="sticky" elevation={1} sx={{ backgroundColor: "#ffffff !important", color: "#000000" }}>
          <Toolbar variant="dense">
            <Typography sx={{ flex: 1, fontWeight: 700, fontSize: "16px" }}>
              OPD History{OpdHistoryColl?.length > 0 ? ` (${OpdHistoryColl.length})` : ""}
            </Typography>
            <IconButton onClick={() => setMobileHistoryOpen(false)} size="small" aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent sx={{ padding: 0 }}>
          {renderOpdHistoryListBody(true)}
        </DialogContent>
      </Dialog>

      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          mx: "auto",
          padding: "16px 8px",
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        {/* LEFT — OPD history for this patient; click to jump/load that OPD */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            width: 220,
            flexShrink: 0,
            position: "sticky",
            top: `${CLOSE_BAR_HEIGHT + 16}px`,
            maxHeight: `calc(100vh - ${CLOSE_BAR_HEIGHT + 32}px)`,
            border: "1px solid #0000001f",
            borderRadius: "8px",
            backgroundColor: "var(--opd-panel-bg)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "var(--opd-header-bg)",
              height: "48px",
              padding: "0px 16px",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
              OPD History{OpdHistoryColl?.length > 0 ? ` (${OpdHistoryColl.length})` : ""}
            </Typography>
          </Box>
          <Box ref={historyListRef} sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
            {renderOpdHistoryListBody(false)}
          </Box>
        </Box>

        {/* MAIN */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {opdBundles.map((bundle, index) => (
            <Box key={bundle.opd.opdId} sx={{ marginTop: index > 0 ? "24px" : 0 }}>
              {renderOpdBlock(bundle)}
            </Box>
          ))}

          <Box ref={bottomSentinelRef} sx={{ height: "1px" }} />

          {/* Loader only at the bottom while the next OPD is loading (no mid-screen loader / skeleton) */}
          {isLoadingNextOpd && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                padding: "20px 0",
              }}
            >
              <CircularProgress size={22} />
              <Typography sx={{ fontSize: "13px", color: "#777" }}>Loading...</Typography>
            </Box>
          )}

          {noMoreOpds && opdBundles.length > 0 && (
            <Typography sx={{ fontSize: "13px", color: "#999", textAlign: "center", padding: "16px" }}>
              End of OPD history
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: 200,
            flexShrink: 0,
            position: "sticky",
            top: `${CLOSE_BAR_HEIGHT + 16}px`,
            border: "1px solid #0000001f",
            borderRadius: "8px",
            backgroundColor: "var(--opd-panel-bg)",
            overflow: "hidden",
          }}
        >
          <List dense disablePadding>
            {PREVIEW_TABS.filter((tab) => tab.key !== "VACCINATION" || activeHasVaccination).map((tab) => (
              <ListItemButton
                key={tab.key}
                selected={activeTab === tab.key}
                onClick={() => scrollToSection(tab.key)}
                sx={{
                  borderLeft: "3px solid transparent",
                  "&.Mui-selected": {
                    borderLeft: "3px solid #1976d2",
                    backgroundColor: "var(--opd-header-bg)",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "var(--opd-header-bg)",
                  },
                }}
              >
                <ListItemText primaryTypographyProps={{ fontSize: "13px", fontWeight: 600 }} primary={tab.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Box>

      {/* ── GROWTH CHART── */}
      <Dialog
        open={openGrowthChartDialog}
        onClose={() => setOpenGrowthChartDialog(false)}
        fullScreen
        aria-labelledby="growth-chart-dialog-title"
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography style={{ fontWeight: 600, marginLeft: 0 }} sx={{ ml: 2, flex: 1 }} variant="h6">
              {`#${growthChartBundle?.opd?.patientNumber || ""} - ${growthChartBundle?.opd?.patientName || ""} `}
              {" - Growth Chart"}
            </Typography>
            <Tooltip title="Close" placement="top">
              <IconButton aria-label="close" onClick={() => setOpenGrowthChartDialog(false)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </AppBar>
        <DialogContent sx={{ padding: "0px 24px 12px 24px" }}>
          <GrowthChart
            opd={growthChartBundle?.opd}
            memberPhysicalGrowthId={memberPhysicalGrowthId}
            isForPopUp={true}
            handleClosePopUp={() => setOpenGrowthChartDialog(false)}
            isDownload={true}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={reportPreviewOpen}
        onClose={() => setReportPreviewOpen(false)}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "4px",
            maxWidth: "950px",
            padding: "0px",
            margin: "0px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #ddd", padding: "4px 16px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                {Math.round(reportZoomLevel * 100)}%
              </Typography>
              <IconButton onClick={handleReportZoomOut} disabled={reportZoomLevel <= 0.25} size="small">
                <RemoveCircleOutlineIcon />
              </IconButton>
              <IconButton onClick={handleReportZoomIn} disabled={reportZoomLevel >= 2} size="small">
                <AddCircleOutlineIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Tooltip title="Print" placement="top">
                <IconButton onClick={handleReportPrint}>
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download" placement="top">
                <IconButton onClick={handleReportDownload}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close" placement="top">
                <IconButton onClick={() => setReportPreviewOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            height: "100%",
            padding: "16px !important",
            fontFamily: "Arial, sans-serif",
            backgroundColor: "#f1f3f5",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              width: "max-content",
              border: "1px solid #ddd",
              borderRadius: "2px",
              padding: "30px 50px",
              fontFamily: "Arial, sans-serif",
              backgroundColor: "var(--opd-panel-bg)",
              boxSizing: "border-box",
              transform: `scale(${reportZoomLevel})`,
              transformOrigin: "center",
              transition: "transform 0.2s ease-in-out",
            }}
          >
            <Box ref={reportPrintRef} sx={{ width: "794px" }}>
              <ReportCertificateHeader
                reportData={reportPreviewData}
                selectedSections={certificateReportConfigDetail.reduce((acc: any, item: any) => {
                  acc[item.configKey] = item.isChecked;
                  return acc;
                }, {})}
              />
              {reportPreviewData?.previewDescription && (
                <div
                  style={{ margin: "10px 16px" }}
                  dangerouslySetInnerHTML={{ __html: reportPreviewData.previewDescription }}
                />
              )}
              <ReportCertificateFooter
                reportData={reportPreviewData}
                selectedSections={certificateReportConfigDetail.reduce((acc: any, item: any) => {
                  acc[item.configKey] = item.isChecked;
                  return acc;
                }, {})}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── PRINT / TEMPLATE-SELECTION / PDF-PREVIEW DIALOGS ── */}
      <OPDClinicalDialogs
        {...({
          userContext,
          isLoading,
          setIsLoading,
          fullScreen,
          mdd,
          smd,
          selectedOPD,
          fetchDetailedOpdforReport,
          handleOpenDialog,
          openTemplateDialog,
          setOpenTemplateDialog,
          prescriptionTemplates,
          selectedPrescriptionTemplate,
          setSelectedPrescriptionTemplate,
          setSelectedReportTemplate,
          handleClickOpenPrescriptionReports,
          saveDefaultPrescription,
          openPrescriptionReport,
          handleCloseHTMLPopup,
          zoomLevel,
          handleZoomChange,
          zoomOptions,
          handleZoomOut,
          handleZoomIn,
          handlePdfPrint,
          handlePDFDownload,
          handleSettingsOpen,
          menuAnchorEl,
          reportConfigData,
          setReportConfigData,
          handleSettingsSave,
          handleSettingsClose,
          htmlprintRef,
          detailedOpd,
          reportConfigDetail,

          openPrescription: { open: false, type: "" },
          handleClosePrescription: () => { },
          prescriptionPrintReportData: "",
          invoiceTitle: "",
          scroll: "paper",
          itemData: [],
          url: "",
          openBilling: false,
          handleCloseInvoice: () => { },
          billingPrintReportData: "",
          billingTitle: "",
          billingurl: "",
          billingData: [],
          prescriptionHtml: null,
          handlePrint: () => { },
          printRef: { current: null },
          openMore: false,
          anchorEl: null,
          handleCloseMore: () => { },
          generateToken: async () => null,
          handlePatientOpdWhatsApp: async () => { },
          handlePatientScheduleWhatsApp: async () => { },
          handleClickOpenBilling: () => { },
          handleOpenInvoiceReportSelctionDialogBox: () => { },
          handleGenerateBill: () => { },
          handleEditBilling: () => { },
          handleOpenOPDClinicDetail: () => { },
          handleCancel: () => { },
          customPopUp: { open: false, name: "", code: "", opd: null },
          handleClosePopup: () => { },
          handleManagePopup: () => { },
          tabType: "ALL",
          invoiceReportSelectionDialog: false,
          handleCloseInvoiceReportSelctionDialogBox: () => { },
          saveDefaultBilling: async () => { },
          templateSelectionPopUp: { type: "", open: false },
          handleCloseTemplateSelection: () => { },
          handleClickOpenPrescription: async () => { },
          isAutoPrescriptionPopUp: false,
          handleCloseAutoPrescription: () => { },
          isAutoPrescriptionChecked: false,
          setIsAutoPrescriptionChecked: () => { },
          handleAcceptAutoPrescription: () => { },
          openWhatsAppDialog: false,
          setOpenWhatsAppDialog: () => { },
          selectedWhatsappTemplateId: "",
          setSelectedWhatsappTemplateId: () => { },
          whatsappTemplates: [],
          selectedWhatsappContext: null,
          setSelectedWhatsappContext: () => { },
          defaultWhatsappTemplates: {},
          handleUpdateDefaultWhatsappTemplate: async () => { },
          whatsappPreviewMessage: "",
          selectedItem: null,
          setSelectedItem: () => { },
          sendVisitCompletionViaApi: async () => { },
          singleSendItem: null,
          singleSendTarget: null,
          openSingleItemTemplateStyleDialog: () => { },
          closeSingleItemTemplateStyleDialog: () => { },
          handleClickOnWhatsApp: async () => { },
          setMissingPhoneNames: () => { },
          openMissingNumberDialog: false,
          setOpenMissingNumberDialog: () => { },
          missingPhoneNames: [],
          openEmailDialog: false,
          setOpenEmailDialog: () => { },
          selectedEmailTemplateId: "",
          setSelectedEmailTemplateId: () => { },
          emailTemplates: [],
          defaultEmailTemplateId: "",
          handleUpdateDefaultEmailTemplate: async () => { },
          emailPreviewMessage: "",
          selectedDoctor: null,
          handleEmailSend: async () => { },
          openMissingEmailDialog: false,
          setOpenMissingEmailDialog: () => { },
          missingEmail: [],
          whatsappQr: { open: false, qr: null, retry: 0 },
          handleCloseWhatsappQrPopup: () => { },
          vitalsCustomPopUP: false,
          handleCloseVitalsCustomPopUp: () => { },
          opd: null,
          openBulkTemplateDialog: false,
          setOpenBulkTemplateDialog: () => { },
          bulkTemplateSelection: "",
          setBulkTemplateSelection: () => { },
          handleBulkSend: async () => { },
          bulkSendTarget: "PATIENT",
          openBillingPopupForHtml: async () => null,
          selectedValue: null,
          setSelectedValue: () => { },
          tempSelectedValue: null,
          setTempSelectedValue: () => { },
          openA4HtmlDialog: false,
          setOpenA4HtmlDialog: () => { },
          detailedBilling: null,
          setDetailedBilling: () => { },
          handleCloseBillingA4HtmlPopup: () => { },
          mainMenuEl: { for: "", target: null },
          setMainMenuEl: () => { },
          handleSettingsBillingA4HtmlOpen: () => { },
          handleSettingsBillingA4HtmlClose: () => { },
          billingReportConfigDetail: [],
          setBillingReportConfigDetail: () => { },
          reportBillingConfigData: [],
          setReportBillingConfigData: () => { },
          handleBillingSettingsSave: async () => { },
          sendReferralDoctorViaApi: async () => { },
        } as any)}
      />
    </>
  );
});

export default OPDClinicalPreview;